/**
 * A day at the beach: in through the gate (a ticket, localised), then whatever you like: rent a lounger under a
 * cabana or umbrella for the afternoon, swim, something from the bar (Lagos: suya, a coconut, a cold drink), a horse
 * ride along the water. It's an outing: fun and social up, energy for the lounger, a little fitness for the swim.
 */
import { advance, spend, type LifeEvent, type LifeState } from './life.ts';
import { hereCountry, localPrice } from './character.ts';
import { policyShare } from './policy.ts';

const BEACHES: Record<string, string> = {
  lagos: 'Atlantic Sands Beach, Lekki', village: 'Plage des Catalans, Marseille', paris: 'Plage des Catalans, Marseille',
  nyc: 'Coney Island Beach, Brooklyn', sf: 'Ocean Beach, San Francisco', chicago: 'Oak Street Beach, Chicago',
  shanghai: 'Jinshan City Beach, Shanghai', beijing: 'Jinshan City Beach, Shanghai', greatwall: 'Jinshan City Beach, Shanghai',
  tokyo: 'Odaiba Seaside Beach, Tokyo', kyoto: 'Odaiba Seaside Beach, Tokyo', fuji: 'Odaiba Seaside Beach, Tokyo',
  florence: 'Bagno Paradiso, Forte dei Marmi', rome: 'Lido di Ostia, Rome', tuscany: 'Bagno Paradiso, Forte dei Marmi',
  dubai: 'Kite Beach, Jumeirah', marina: 'JBR Beach, Dubai Marina', deira: 'Kite Beach, Jumeirah',
};
export function beachFor(region: string, hood: string): string {
  return BEACHES[region === 'lagos' ? 'lagos' : hood] ?? BEACHES[({ france: 'village', us: 'nyc', china: 'shanghai', japan: 'tokyo' } as Record<string, string>)[region] ?? 'lagos'];
}
export const beachEntry = (s: LifeState) => localPrice(s, 3_000 * policyShare(hereCountry(s), 'attraction_fees'), 500);
export const loungerPrice = (s: LifeState) => localPrice(s, 10_000, 500);
export const horsePrice = (s: LifeState) => localPrice(s, 5_000, 500);

export interface BeachDay { since: number; lounger: boolean; swims: number; horse: boolean; snacks: number }
export const beachOf = (s: LifeState): BeachDay | null => (s.beach && s.minutes - s.beach.since < 12 * 60 ? s.beach : null);

export function enterBeach(s: LifeState): { ok: boolean; line: string } {
  if (beachOf(s)) return { ok: true, line: 'Back in on your wristband.' };
  if (!spend(s, beachEntry(s), 'Beach entry')) return { ok: false, line: `Entry is ${beachEntry(s).toLocaleString()}. You don't have it.` };
  s.beach = { since: s.minutes, lounger: false, swims: 0, horse: false, snacks: 0 };
  return { ok: true, line: 'A wristband snapped on. The sand is hot; the sea is loud.' };
}

export type BeachThing = 'lounger' | 'swim' | 'horse' | 'suya' | 'coconut' | 'drink';
const SNACK: Record<'suya' | 'coconut' | 'drink', [number, string, number, string]> = {
  suya: [2_500, 'A stick of suya, pepper and onions, wrapped in newspaper.', 18, 'Suya'],
  coconut: [1_500, 'A fresh coconut, the top hacked off with a machete, a straw stuck in.', 8, 'Coconut'],
  drink: [1_000, 'A cold bottle from the cooler, sweating in the heat.', 4, 'Cold drink'],
};
export const snackPrice = (s: LifeState, k: 'suya' | 'coconut' | 'drink') => localPrice(s, SNACK[k][0], 100);

export function atBeach(s: LifeState, what: BeachThing): { ok: boolean; line: string; events: LifeEvent[] } {
  const b = beachOf(s);
  if (!b) return { ok: false, line: 'Get a ticket at the gate first.', events: [] };
  const need = (k: 'fun' | 'energy' | 'social' | 'hygiene' | 'hunger', v: number) => { s.needs[k] = Math.max(0, Math.min(100, s.needs[k] + v)); };
  let line = '', mins = 10;
  if (what === 'lounger') {
    if (!b.lounger) { if (!spend(s, loungerPrice(s), 'Beach lounger')) return { ok: false, line: "You can't afford the lounger.", events: [] }; b.lounger = true; }
    mins = 60; need('energy', 14); need('fun', 8);
    line = 'An hour in the shade, the wind off the sea, the music from the shack, half asleep.';
  } else if (what === 'swim') {
    b.swims++; mins = 20; need('fun', b.swims > 2 ? 4 : 10); need('energy', -6); need('hygiene', -4);
    s.skills.fitness = Math.min(100, s.skills.fitness + 0.5);
    line = b.swims === 1 ? 'The first wave knocks you sideways. The second one you dive under. The water is warm.' : 'Another swim, out to where your feet just touch.';
  } else if (what === 'horse') {
    if (!spend(s, horsePrice(s), 'Horse ride')) return { ok: false, line: "You can't afford the ride.", events: [] };
    b.horse = true; mins = 20; need('fun', 14);
    line = 'The handler leads you down the waterline, the horse splashing through the surf. Hold the saddle, not the reins.';
  } else {
    const [, l, hunger, label] = SNACK[what];
    if (!spend(s, snackPrice(s, what), label)) return { ok: false, line: "You can't afford it.", events: [] };
    b.snacks++; need('hunger', hunger); need('fun', 3); line = l;
  }
  return { ok: true, line, events: advance(s, mins, { busy: true }) };
}

/** Leaving: what the day was. */
export function leaveBeach(s: LifeState): string[] {
  const b = beachOf(s);
  if (!b) return [];
  s.needs.social = Math.min(100, s.needs.social + 6);
  s.stats.beachDays = (s.stats.beachDays ?? 0) + 1;
  if (s.stats.beachDays === 1) s.history.push({ t: s.minutes, text: 'A first day at the beach.' });
  const lines = [b.swims ? `${b.swims === 1 ? 'A swim' : `${b.swims} swims`} in the sea.` : 'You never went in the water.'];
  if (b.horse) lines.push('A horse ride along the surf.');
  if (b.lounger) lines.push('An afternoon on a lounger.');
  s.beach = undefined;
  return lines;
}
